import { logError } from '@/lib/sentry.lib';
import { useMemo, useState } from 'react';
import { useCoworkerProfileStore } from '@/store/coworker-preferences.store';
import { API } from '@repo/hooks';
import { translate } from '@repo/phrase';
import type { AssetDataType, AssetType } from '@repo/types/asset';
import { isDraft } from '@repo/types/draft';
import { isOrder } from '@repo/types/order';
import { isQuotation } from '@repo/types/quotation';
import type { CustomerDetails } from '@repo/utils';
import { getAppLocale, getNotifier } from '@repo/utils';
import { isDefined, isEmpty } from '@repo/utils/type.utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { logTelemetry, type ShareType, TELEMETRY_LOGS } from '@/lib/telemetry.lib';
import type { ShareOption } from '@/components/share/share-options';
import { getSupportedLocaleOptions, isSelectedLocaleValid } from '@/components/share/share-options';
import { shareEvent, type ShareEventType } from '@/api/asset.api';
import { useDefaultPrinter } from '@/hooks/asset.hooks';
import { canPrintTemsAndCondtion, isEligibleForTermsOfConditions } from '@/utils/asset.utils';
import { shareAsset } from '@/utils/share.utils';
import { getAppConfig } from '@/utils/window.utils';

interface ShareFormProps {
    data: AssetDataType;
    initialEmail?: string | undefined;
    initialSms?: string | undefined;
    preferredLocale?: string | undefined;
    storePrimaryLocale?: string | undefined;
}

export const useShareForm = ({
    data,
    initialEmail,
    initialSms,
    preferredLocale,
    storePrimaryLocale,
}: ShareFormProps) => {
    const config = getAppConfig();
    const emailEnabled = config.shareOptions.emailEnabledFor.includes(data.assetType);
    const printEnabled = config.shareOptions.printEnabledFor.includes(data.assetType);
    const isDraftOrder = isDraft(data);
    const isQuotationOrder = isQuotation(data);
    const supportedLocales = getSupportedLocaleOptions(preferredLocale, storePrimaryLocale);
    const customerLocale = supportedLocales.find(({ id }) => id === preferredLocale)?.id;

    const [email, setEmail] = useState<ShareOption>({
        type: 'email',
        value: initialEmail,
        checked: emailEnabled && isDefined(initialEmail) && !isDraftOrder && !isQuotationOrder,
    });
    const [sms, setSms] = useState<ShareOption>({
        type: 'sms',
        value: initialSms,
        checked: false,
    });
    const [printerSelection, setPrinter] = useState<ShareOption>({
        type: 'print',
        value: undefined,
        noOfCopies: 1,
        printTermsAndConditions: false,
        checked: false,
    });
    const defaultPrinterValue = useDefaultPrinterValue(printEnabled);
    const printer = {
        ...printerSelection,
        value: printerSelection.value ?? defaultPrinterValue,
    };

    const [selectedLocale, setSelectedLocale] = useState<string | undefined>();
    const locale = selectedLocale ?? customerLocale ?? storePrimaryLocale;

    const [shouldValidate, setShouldValidate] = useState(false);
    const isLocaleValid = isDraftOrder || isSelectedLocaleValid(supportedLocales, locale);
    const isValidForm =
        (email.checked ? !isEmpty(email.value) : true) &&
        (sms.checked ? !isEmpty(sms.value) : true) &&
        (printer.checked ? !isEmpty(printer.value) && printer.noOfCopies !== 0 : true) &&
        isLocaleValid;
    const hasSelectedShareOption = email.checked || sms.checked || printer.checked;

    const validate = () => {
        setShouldValidate(true);

        return isValidForm;
    };

    return {
        email,
        hasSelectedShareOption,
        locale,
        printer,
        setEmail,
        setLocale: setSelectedLocale,
        setPrinter,
        setSms,
        shouldValidate,
        sms,
        supportedLocales,
        validate,
    };
};

interface Props {
    orderId: string;
    assetType: AssetType;
    data: AssetDataType;
    email: ShareOption;
    sms: ShareOption;
    printer: ShareOption;
    locale?: string | undefined;
    customerDetails: CustomerDetails;
    onClose: () => void;
    eventType: ShareEventType;
}

export const useTermsAndConditions = (data: AssetDataType) => {
    const isOrderAsset = isOrder(data);
    const coworkerPreferences = useCoworkerProfileStore((state) => state.coworkerPreferences);
    const storePreferencesConfig = useCoworkerProfileStore((state) => state.storePreferencesConfig);

    const termsAndConditions = useMemo(() => {
        if (!coworkerPreferences) {
            return false;
        }
        const canPrint = canPrintTemsAndCondtion(storePreferencesConfig?.preferences);
        const isEligible = isOrderAsset && isEligibleForTermsOfConditions(data);

        return isEligible && canPrint;
    }, [data, coworkerPreferences, storePreferencesConfig, isOrderAsset]);

    return termsAndConditions;
};

export const useDefaultPrinterValue = (enabled = true): string | undefined => {
    const coworkerPreferences = useCoworkerProfileStore((state) => state.coworkerPreferences);
    const msalProfile = useCoworkerProfileStore((state) => state.msalProfile);
    const buCode = coworkerPreferences?.buCode ?? '';
    const mailNickname = msalProfile?.mailNickname ?? '';
    const { data } = useDefaultPrinter(buCode, mailNickname, {
        enabled: enabled && Boolean(buCode) && Boolean(mailNickname),
    });
    const defaultPrinter = data?.defaultPrinter;

    if (!defaultPrinter?.name || !defaultPrinter.serverName) {
        return;
    }

    return `${defaultPrinter.serverName}|${defaultPrinter.name}`;
};

const isShareChannelSelected = (shareOption: ShareOption): boolean => shareOption.checked && Boolean(shareOption.value);

export const useShareMutation = ({
    orderId,
    assetType,
    data,
    email,
    sms,
    printer,
    locale,
    customerDetails,
    eventType,
    onClose,
}: Props) => {
    const notifier = getNotifier();
    const termsAndConditions = useTermsAndConditions(data);
    const queryClient = useQueryClient();
    const appLocale = getAppLocale();

    const shareInLocale = locale ?? appLocale;
    const emailSelected = isShareChannelSelected(email);
    const smsSelected = isShareChannelSelected(sms);
    const printSelected = isShareChannelSelected(printer);

    const selectedShareTypes: ShareType[] = [
        emailSelected ? 'email' : undefined,
        smsSelected ? 'sms' : undefined,
        printSelected ? 'print' : undefined,
    ].filter((shareType): shareType is ShareType => shareType !== undefined);

    const shareOrderMutation = useMutation({
        mutationFn: () =>
            shareAsset({
                assetType,
                customerDetails,
                data,
                email,
                locale: shareInLocale,
                orderId,
                printer,
                sms,
                termsAndConditions,
                eventType,
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                predicate: (query) =>
                    query.queryKey[0] === API.shareOrder ||
                    query.queryKey[0] === API.shareQuotation ||
                    query.queryKey[0] === API.defaultPrinter ||
                    query.queryKey[0] === API.favouritePrinterList,
            });

            onClose();

            notifier?.add({
                title: translate(`dialog_share_notification_title_${assetType}`),
                message: translate(`dialog_share_notification_body_${assetType}`, {
                    number: orderId,
                }),
            });
        },
        onError: (err) => {
            onClose();

            notifier?.add({
                title: translate(`dialog_share_notification_error_title_${assetType}`),
                message: translate(`dialog_share_notification_error_body_${assetType}`),
                type: 'error',
            });

            logError(err);
        },
        onSettled: (_data, error) => {
            const result = error ? 'failed' : 'success';

            for (const shareType of selectedShareTypes) {
                logTelemetry(error ? 'error' : 'info', TELEMETRY_LOGS.AssetShare, {
                    share: { shareType, result },
                });
            }
        },
    });

    return shareOrderMutation;
};

export const useShareEventType = (data: AssetDataType): ShareEventType => {
    const isQuotationOrder = isQuotation(data);

    // only for updated quotation order at the moment
    if (isQuotationOrder && data.assetDetails.status === 'UPDATED') {
        return shareEvent.UPDATE;
    }

    return shareEvent.CREATE;
};
